/**
 * BacklogCommentsSection — Backlog コメントを WBS 詳細パネルに表示する。
 *
 * 表示順 = 新しい順 (Backlog `order=desc`). 起動時のフローは:
 *   1. project の backlogCache.recentComments (≤3 件) を即時に出して
 *      callable 応答までの空白を避ける（fallback でもある）。
 *   2. 並行して `fetchBacklogComments` callable を投げて 100 件取り直す。
 *      成功したらキャッシュ表示を置換、失敗したらキャッシュ表示のまま
 *      silent fallback + 小さいエラーメッセージ。
 *   3. 100 件返ってきて hasMore=true なら「もっと読む」ボタン。クリックで
 *      最後のコメント commentId をそのまま `maxId` に渡し、追加 100 件を
 *      append する（Backlog は id < maxId を返す）。
 *
 * issueKey が変わったらすべて reset + 再フェッチ。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquare, RefreshCw } from 'lucide-react';
import type { BacklogCachedComment } from '@mct/shared';
import {
  fetchBacklogComments,
  type FetchedBacklogComment,
} from '../../lib/backlogComments';
import { formatRelativeFromNow } from '../../lib/relativeTime';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { BacklogImageContent } from './BacklogImageContent';

export interface BacklogCommentsSectionProps {
  projectId: string;
  issueKey: string;
  /**
   * BacklogCacheDoc.recentComments — Firestore から読み出した最新 ≤3 件
   * のキャッシュ。callable 応答前 / 失敗時の fallback として表示する。
   */
  recentComments: readonly BacklogCachedComment[];
}

interface DisplayComment {
  commentId: string;
  authorName: string;
  body: string;
  createdAtMillis: number;
  createdAtDate: Date;
}

function fromCached(c: BacklogCachedComment): DisplayComment {
  const date = c.createdAt.toDate();
  return {
    commentId: c.commentId,
    authorName: c.authorName,
    body: c.body,
    createdAtMillis: date.getTime(),
    createdAtDate: date,
  };
}

function fromFetched(c: FetchedBacklogComment): DisplayComment {
  const date = new Date(c.createdAt);
  return {
    commentId: String(c.commentId),
    authorName: c.authorName,
    body: c.body,
    createdAtMillis: date.getTime(),
    createdAtDate: date,
  };
}

const PAGE_SIZE = 100;

export function BacklogCommentsSection({
  projectId,
  issueKey,
  recentComments,
}: BacklogCommentsSectionProps): JSX.Element {
  // Seed with the Firestore cache so the section renders something
  // immediately (the callable round-trip can take 500-1500ms).
  const initial = useMemo(
    () => recentComments.map(fromCached),
    [recentComments],
  );
  const [comments, setComments] = useState<DisplayComment[]>(initial);
  // Once the callable returns, treat that as the canonical list — the
  // cache fallback should never overwrite freshly-fetched data even if
  // the BacklogCacheDoc changes between renders.
  const [fetched, setFetched] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset everything on issue switch — also on `projectId` change for
  // completeness, though in practice the panel won't switch projects.
  useEffect(() => {
    setComments(initial);
    setFetched(false);
    setHasMore(false);
    setError(null);
    setLoadingInitial(true);
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchBacklogComments({
          projectId,
          issueKey,
          count: PAGE_SIZE,
        });
        if (cancelled) return;
        if (res.ok) {
          setComments(res.comments.map(fromFetched));
          setHasMore(res.hasMore);
          setFetched(true);
          setError(null);
        } else {
          setError('全件取得に失敗しました（キャッシュのみ表示）');
        }
      } catch (err) {
        if (cancelled) return;
        // Callable threw (network / unauthenticated / HttpsError) —
        // surface a retry affordance instead of leaving the spinner
        // spinning forever (the cache fallback is still rendered).
        // eslint-disable-next-line no-console
        console.warn('fetchBacklogComments threw', err);
        setError('コメントの取得に失敗しました（キャッシュのみ表示）');
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `initial` is included intentionally — when the parent Firestore
    // doc updates between renders, we should re-seed the placeholder.
  }, [projectId, issueKey, initial]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    const oldest = comments[comments.length - 1];
    if (!oldest) return;
    const oldestId = Number(oldest.commentId);
    if (!Number.isFinite(oldestId)) return;
    setLoadingMore(true);
    try {
      const res = await fetchBacklogComments({
        projectId,
        issueKey,
        count: PAGE_SIZE,
        maxId: oldestId,
      });
      if (res.ok) {
        const next = res.comments.map(fromFetched);
        setComments((prev) => {
          const seen = new Set(prev.map((c) => c.commentId));
          return [...prev, ...next.filter((c) => !seen.has(c.commentId))];
        });
        setHasMore(res.hasMore);
        setError(null);
      } else {
        setError('続きの取得に失敗しました');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('fetchBacklogComments(loadMore) threw', err);
      setError('続きの取得に失敗しました');
    } finally {
      setLoadingMore(false);
    }
  }, [comments, projectId, issueKey, loadingMore]);

  const cachedOnlyFallback = !fetched && error !== null;

  return (
    <section
      className="mb-4 rounded-md border border-slate-200 p-3"
      data-testid="wbs-backlog-comments"
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600">
          <MessageSquare className="h-3.5 w-3.5" aria-hidden />
          Backlog コメント
        </h3>
        {loadingInitial ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] text-slate-400"
            data-testid="wbs-backlog-comments-loading"
          >
            <Spinner className="h-3 w-3" /> 読み込み中…
          </span>
        ) : null}
      </div>
      {error ? (
        <p
          className="mb-2 text-[11px] text-amber-700"
          data-testid="wbs-backlog-comments-error"
        >
          {error}
          {cachedOnlyFallback ? (
            <button
              type="button"
              onClick={() => {
                setError(null);
                setLoadingInitial(true);
                void (async () => {
                  try {
                    const res = await fetchBacklogComments({
                      projectId,
                      issueKey,
                      count: PAGE_SIZE,
                    });
                    if (res.ok) {
                      setComments(res.comments.map(fromFetched));
                      setHasMore(res.hasMore);
                      setFetched(true);
                    } else {
                      setError('全件取得に失敗しました（キャッシュのみ表示）');
                    }
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn('fetchBacklogComments(retry) threw', err);
                    setError('コメントの取得に失敗しました（キャッシュのみ表示）');
                  } finally {
                    setLoadingInitial(false);
                  }
                })();
              }}
              className="ml-1 inline-flex items-center gap-0.5 text-indigo-600 hover:text-indigo-800"
            >
              <RefreshCw className="h-3 w-3" /> 再試行
            </button>
          ) : null}
        </p>
      ) : null}
      {comments.length === 0 ? (
        <p
          className="text-[11px] text-slate-400"
          data-testid="wbs-backlog-comments-empty"
        >
          {loadingInitial ? '' : 'コメントはまだありません'}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="wbs-backlog-comments-list">
          {comments.map((c) => (
            <li
              key={c.commentId}
              data-testid="wbs-backlog-comment"
              className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5 text-[11px]"
            >
              <div className="mb-0.5 flex items-baseline justify-between gap-2">
                <span className="font-semibold text-slate-700">
                  {c.authorName}
                </span>
                <span
                  className="text-[10px] text-slate-400"
                  title={c.createdAtDate.toLocaleString('ja-JP')}
                >
                  {formatRelativeFromNow(c.createdAtDate)}
                </span>
              </div>
              <BacklogImageContent
                projectId={projectId}
                issueKey={issueKey}
                text={c.body}
              />
              {/* whitespace-pre-wrap は <BacklogImageContent> 側で適用 */}
            </li>
          ))}
        </ul>
      )}
      {hasMore ? (
        <div className="mt-2 text-center">
          <Button
            variant="secondary"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="!py-1 !text-xs"
            data-testid="wbs-backlog-comments-load-more"
          >
            {loadingMore ? (
              <span className="inline-flex items-center gap-1">
                <Spinner className="h-3 w-3" /> 取得中…
              </span>
            ) : (
              'もっと読む'
            )}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
